import { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../axiosInstance';

const DOCUMENTS_BASE = '/documents';

const SUBTYPE_LABELS = {
  sales_invoice: 'Sales Invoice',
  credit_invoice: 'Credit Invoice',
  debit_notes: 'Debit Note',
  flight_voucher: 'Flight Voucher',
  hotel_voucher: 'Hotel Voucher',
  transfer_voucher: 'Transfer Voucher',
  car_rental_voucher: 'Car Rental Voucher',
  visa_voucher: 'Visa Confirmation',
  cruise_voucher: 'Cruise Voucher',
  booking_confirmation: 'Booking Confirmation',
  insurance_confirmation: 'Insurance Confirmation',
  ssr_confirmation: 'SSR Confirmation',
  payment_request: 'Payment Request',
  payment_reminder: 'Payment Reminder',
};

const STATUS_LABELS = { '0': 'Issued', '1': 'Sent', '2': 'Draft' };

const parseDetails = (value) => {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value) || {};
  } catch {
    return {};
  }
};

export const getSubtypeLabel = (subtype) => SUBTYPE_LABELS[subtype] || subtype;
export const getStatusLabel = (status) => STATUS_LABELS[status] || status;

const toNum = (v) => (v === null || v === undefined || v === '' ? null : parseFloat(v));

export const mapDocumentToUi = (d) => ({
  id: d.id,
  documentNumber: d.document_number || '—',
  documentType: d.document_type,
  documentSubtype: d.document_subtype,
  subtypeLabel: getSubtypeLabel(d.document_subtype),
  bookingReference: d.booking_reference,
  customerName: d.customer_name,
  issueDate: d.issue_date,
  status: d.status,
  statusLabel: getStatusLabel(d.status),
  amount: toNum(d.amount),
  currency: d.currency || 'EUR',
  // Generation timestamps — drive the "last generated" / "booking changed since"
  // freshness hints in the Documents tab. (model is `underscored`, so snake_case.)
  updatedAt: d.updated_at || d.updatedAt || null,
  createdAt: d.created_at || d.createdAt || null,
  // ── Invoice-specific fields (sales_invoice / credit_invoice / debit_notes) ──
  vatType: d.vat_type || null,
  subtotal: toNum(d.subtotal),
  vatAmount: toNum(d.vat_amount),
  tomsMargin: toNum(d.toms_margin),
  linkedInvoiceNumber: d.linked_invoice_number || null,
  // Payment requests and reminders: the date the balance is (or was) due.
  dueDate: parseDetails(d.voucher_details).due_date || null,
  reason: d.reason || '',
  pdfUrl: d.pdf_url || null,
  notes: d.notes || '',
  flightDetails: d.flightDetails || [],
  travelers: d.travelers || [],
  _raw: d,
});

export const useGetBookingDocuments = (bookingReference) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // `silent` reconciles against the server WITHOUT toggling the full-screen
  // loading state or clobbering the view with an error — used right after a
  // generate/regenerate so the list refreshes with no skeleton flash, and a
  // transient GET failure never hides documents that were just generated.
  // Resolves to `true` when the list was refreshed, `false` on failure — callers
  // (e.g. the generate flow) use this to avoid a misleading "success" after a
  // reconcile that silently failed.
  const fetchDocuments = useCallback(async ({ silent = false } = {}) => {
    if (!bookingReference) return false;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const response = await axiosInstance.get(`${DOCUMENTS_BASE}/booking/${bookingReference}`);
      const res = response.data;
      if (res?.success) {
        setData((res.data || []).map(mapDocumentToUi));
        return true;
      }
      if (!silent) setError(res?.message || 'Failed to load documents');
      return false;
    } catch (err) {
      if (!silent) setError(err.response?.data?.message || err.message || 'Failed to load documents');
      return false;
    } finally {
      if (!silent) setLoading(false);
    }
  }, [bookingReference]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  // Stable identity so a consumer's useCallback that depends on it isn't recreated
  // every render (which would defeat its own memoization).
  const refetchSilent = useCallback(() => fetchDocuments({ silent: true }), [fetchDocuments]);

  return {
    data,
    setData,
    loading,
    error,
    refetch: fetchDocuments,
    refetchSilent,
  };
};

export const useGenerateVouchers = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (bookingReference) => {
    if (!bookingReference) return { success: false, message: 'Booking reference required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.post(`${DOCUMENTS_BASE}/generate/${bookingReference}?force=true`, {}, { timeout: 30000 });
      const res = response.data;
      if (res?.success) return { success: true, data: res.data, message: res.message };
      setError(res?.message || 'Failed to generate vouchers');
      return { success: false, message: res?.message };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to generate vouchers';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

// ─────────────────────────────────────────────────────────────────────────────
// INVOICE GENERATION
//
// Feature flag: invoice generation from booking data (Pricing & VAT → Sales
// Invoice) is not yet wired on the backend. The UI is fully built and the hook
// below is ready to call the planned endpoint — flip this flag to `true` once
// `POST /documents/generate-invoice/:booking_reference` is live and the
// Invoices panel will start generating for real with no further UI changes.
// ─────────────────────────────────────────────────────────────────────────────
export const INVOICE_GENERATION_ENABLED = true;

// Single "Generate Invoice" entry point. The backend (generateInvoiceFromBooking)
// smart-routes by product family — flight/mixed → flight FACTUUR, otherwise it
// delegates to the hotel / transfer / car-rental "X only" generator — so the UI
// needs just this one call for every booking type.
export const useGenerateInvoices = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // `force` (default true) makes the backend REFRESH an existing per-family invoice
  // in place — same immutable number, rebuilt from the latest booking data + a new
  // PDF — instead of returning the stale "already exists" copy. This is what powers
  // "Regenerate": a first generation has nothing to force, so true is always safe.
  const execute = useCallback(async (bookingReference, { force = true } = {}) => {
    if (!bookingReference) return { success: false, message: 'Booking reference required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.post(
        `${DOCUMENTS_BASE}/generate-invoice/${bookingReference}${force ? '?force=true' : ''}`,
        {},
        { timeout: 30000 }
      );
      const res = response.data;
      if (res?.success) return { success: true, data: res.data, message: res.message };
      setError(res?.message || 'Failed to generate invoice');
      return { success: false, message: res?.message };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to generate invoice';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

// Booking confirmation (§8.3.1) — one per booking, built from the same section
// data the invoice uses. `force` refreshes the snapshot and the PDF in place,
// keeping the document's immutable number, which is what "Regenerate" needs.
export const useGenerateBookingConfirmation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (bookingReference, { force = true } = {}) => {
    if (!bookingReference) return { success: false, message: 'Booking reference required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.post(
        `${DOCUMENTS_BASE}/generate-booking-confirmation/${bookingReference}${force ? '?force=true' : ''}`,
        {},
        { timeout: 30000 }
      );
      const res = response.data;
      if (res?.success) return { success: true, data: res.data, message: res.message };
      setError(res?.message || 'Failed to generate booking confirmation');
      return { success: false, message: res?.message };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to generate booking confirmation';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

// SSR / preferences confirmation (§8.3.2) — one per booking, listing every
// special service request with the status the airline gave it.
export const useGenerateSSRConfirmation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (bookingReference, { force = true } = {}) => {
    if (!bookingReference) return { success: false, message: 'Booking reference required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.post(
        `${DOCUMENTS_BASE}/generate-ssr-confirmation/${bookingReference}${force ? '?force=true' : ''}`,
        {},
        { timeout: 30000 }
      );
      const res = response.data;
      if (res?.success) return { success: true, data: res.data, message: res.message };
      setError(res?.message || 'Failed to generate services confirmation');
      return { success: false, message: res?.message };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to generate services confirmation';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

// Insurance confirmation (§8.3.3) — one per booking, covering every insurance
// product on it.
export const useGenerateInsuranceConfirmation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (bookingReference, { force = true } = {}) => {
    if (!bookingReference) return { success: false, message: 'Booking reference required' };
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.post(
        `${DOCUMENTS_BASE}/generate-insurance-confirmation/${bookingReference}${force ? '?force=true' : ''}`,
        {},
        { timeout: 30000 }
      );
      const res = response.data;
      if (res?.success) return { success: true, data: res.data, message: res.message };
      setError(res?.message || 'Failed to generate insurance confirmation');
      return { success: false, message: res?.message };
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to generate insurance confirmation';
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

// Payment requests and overdue-payment reminders (Documents spec §15). The
// amount is always the booking's current outstanding balance from Finance;
// the status call says what may be issued now and why not.
export const fetchPaymentRequestStatus = async (bookingReference) => {
  const response = await axiosInstance.get(`${DOCUMENTS_BASE}/payment-requests/${bookingReference}`);
  return response.data?.data || null;
};

const usePaymentDocumentAction = (path, fallbackMessage) => {
  const [loading, setLoading] = useState(false);

  const execute = useCallback(async (bookingReference, body = {}) => {
    if (!bookingReference) return { success: false, message: 'Booking reference required' };
    setLoading(true);
    try {
      const response = await axiosInstance.post(`${DOCUMENTS_BASE}/${path}/${bookingReference}`, body, { timeout: 30000 });
      const res = response.data;
      if (res?.success) return { success: true, data: res.data, message: res.message };
      return { success: false, message: res?.message || fallbackMessage };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || err.message || fallbackMessage };
    } finally {
      setLoading(false);
    }
  }, [path, fallbackMessage]);

  return { execute, loading };
};

export const useGeneratePaymentRequest = () =>
  usePaymentDocumentAction('generate-payment-request', 'Failed to issue the payment request');

export const useGeneratePaymentReminder = () =>
  usePaymentDocumentAction('generate-payment-reminder', 'Failed to issue the payment reminder');

// PDF downloads and sending moved to the Documents centre API
// (documentCentreApi.js), which applies the release checks (Documents spec §10).

// Cap on how long the UI keeps checking for the background object-storage
// upload to finish so it can swap in the stored (CDN) URL. Download already
// works on demand regardless, so this is a quiet best-effort upgrade only.
const MAX_POLL_ATTEMPTS = 6;

/**
 * Poll for PDF readiness on document IDs where pdf_url is null.
 * Once all are ready (or polling times out), stop. Updates parent data via setData.
 *
 * If the background OVH upload never finishes, pending docs are tagged
 * `pdfStuck: true` so the UI can fall back to the on-demand /:id/pdf endpoint
 * instead of leaving the user with a frozen "Preparing..." button.
 */
export const usePollPdfStatus = (documents, setData) => {
  const timerRef = useRef(null);
  const mountedRef = useRef(true);
  const documentsRef = useRef(documents);
  documentsRef.current = documents;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const startPolling = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    let attempts = 0;

    const markStuck = (ids) => {
      const stuck = new Set(ids);
      setData(prev => prev.map(doc => (stuck.has(doc.id) ? { ...doc, pdfStuck: true } : doc)));
    };

    const poll = async () => {
      if (!mountedRef.current) return;

      const pending = documentsRef.current.filter(d => !d.pdfUrl && !d.pdfStuck);
      if (pending.length === 0) return;
      attempts++;

      try {
        const ids = pending.map(d => d.id).join(',');
        const response = await axiosInstance.get(`${DOCUMENTS_BASE}/pdf-status?ids=${ids}`);
        const res = response.data;

        if (res?.success && res.data) {
          const statusMap = res.data;

          setData(prev => {
            let changed = false;
            const next = prev.map(doc => {
              const info = statusMap[doc.id];
              if (info && info.ready && !doc.pdfUrl) {
                changed = true;
                return { ...doc, pdfUrl: info.pdfUrl, pdfStuck: false };
              }
              return doc;
            });
            // Keep the SAME array reference when nothing changed: a no-progress
            // poll round must not change `documents` identity, otherwise the
            // consumer's polling effect restarts and resets `attempts`, making the
            // MAX_POLL_ATTEMPTS cap / pdfStuck fallback unreachable for a truly
            // stuck upload.
            return changed ? next : prev;
          });

          const stillPending = pending.filter(d => {
            const info = statusMap[d.id];
            return !info || !info.ready;
          });

          if (stillPending.length === 0) return;

          if (attempts >= MAX_POLL_ATTEMPTS) {
            markStuck(stillPending.map(d => d.id));
            return;
          }
          if (mountedRef.current) timerRef.current = setTimeout(poll, 2000);
        }
      } catch {
        if (attempts >= MAX_POLL_ATTEMPTS) {
          markStuck(pending.map(d => d.id));
          return;
        }
        if (mountedRef.current) timerRef.current = setTimeout(poll, 3000);
      }
    };

    timerRef.current = setTimeout(poll, 1500);
  }, [setData]);

  const stopPolling = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  return { startPolling, stopPolling };
};

